/**
 * Session Log Tab Component
 * Displays user's login history
 */

import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useUserSessions } from '../hooks';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface SessionLogTabProps {
  userId: string;
}

export function SessionLogTab({ userId }: SessionLogTabProps) {
  const { data: sessions, isLoading, error } = useUserSessions(userId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Failed to load session log
      </div>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No login history
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date & Time</TableHead>
            <TableHead>IP Address</TableHead>
            <TableHead>User Agent</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((session) => (
            <TableRow key={session._id}>
              <TableCell className="font-mono text-sm">
                {format(new Date(session.login_at), 'MMM d, yyyy h:mm a')}
              </TableCell>
              <TableCell className="font-mono text-sm">
                {session.ip || '—'}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">
                {session.user_agent || '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
